import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Snackbar,
    Alert,
    Tabs,
    Tab,
    IconButton,
} from "@mui/material";
import axios from 'axios';

// Custom hooks
import { useParams, useNavigate } from "react-router-dom";
import { withAuth, useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";
import { useProductMedia } from "@/hooks/products/useProductMedia";
import { useProductAttributes } from "@/hooks/products/useProductAttributes";
import { useProductVariants } from "@/hooks/products/useProductVariants";

// Components
import LayoutCms from "@/theme/layout-cms";
import Icon from "@/theme/vectors/bundle";
import SlidePanel from "@/theme/partials/slidepanel";
import ProductInfoTab from "@/components/cms/products/ProductInfoTab";
import ProductConfigTab from "@/components/cms/products/ProductConfigTab";
import MediaPanel from "@/components/cms/products/MediaPanel";
import AttributesPanel from "@/components/cms/products/AttributesPanel";
import DeleteConfirmationDialog from "@/components/cms/products/DeleteConfirmationDialog";
import AttributeConfirmDialog from "@/components/cms/products/AttributeConfirmDialog";
import PageHeader from "@/components/cms/common/PageHeader";

// Constants
import { API_BASE_URL } from "@/utils/products/constants";

// TabPanel component for tabs
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
  
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const CmsProductDetailPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    
    // Tab state
    const [tabValue, setTabValue] = useState(0);
    
    // Product state
    const [product, setProduct] = useState({
        productId: "",
        headline: "",
        price: "",
        content: "",
        medias: [],
        attributes: [],
        variants: [],
    });
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
    
    // Thêm state theo dõi trạng thái ban đầu của slug
    const [initiallyHadSlug, setInitiallyHadSlug] = useState(false);
    
    // Media Panel state
    const { isOpen: isMediaPanelOpen, openPanel: openMediaPanel, closePanel: closeMediaPanel } = SlidePanel.useSlidePanel();
    
    // Attributes Panel state
    const { isOpen: isAttributesPanelOpen, openPanel: openAttributesPanel, closePanel: closeAttributesPanel } = SlidePanel.useSlidePanel();
    
    // Delete Confirmation Dialog state
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        mediaId: null,
        filename: null,
        isLoading: false
    });
    
    // Add state for confirmation dialog
    const [attributeConfirmDialog, setAttributeConfirmDialog] = useState({
        open: false,
        mode: 'save' // or 'discard'
    });
    
    // Active variant state for selecting media
    const [activeVariantId, setActiveVariantId] = useState(null);
    
    // Media custom hook
    const { 
        fileInputRef,
        selectedFiles, 
        uploading, 
        handleFileSelect, 
        handleFileChange, 
        handleFileUpload, 
        handleRemoveMedia, 
        handleCancelUpload, 
        handleClearSelectedFiles 
    } = useProductMedia(product, setProduct, showNotification);
    
    // Attributes custom hook
    const {
        tempAttributes,
        setTempAttributes,
        tempVariants,
        setTempVariants,
        unsavedAttributeChanges,
        setUnsavedAttributeChanges,
        newAttribute,
        setNewAttribute,
        editingAttributeIndex,
        setEditingAttributeIndex,
        handleAddAttribute,
        handleEditAttribute,
        handleRemoveAttribute,
        handleCancelEdit,
        handleAttributeInputChange,
        handleSaveAttributes
    } = useProductAttributes(product, setProduct, showNotification);
    
    // Variants custom hook
    const {
        tempPriceValue,
        editingPriceVariantId,
        tempPrediscountPriceValue,
        editingPrediscountPriceVariantId,
        handleVariantChange,
        handlePriceCellClick,
        handlePriceChange,
        handlePriceUpdate,
        handlePriceKeyPress,
        handlePrediscountPriceCellClick,
        handlePrediscountPriceChange,
        handlePrediscountPriceUpdate,
        handlePrediscountPriceKeyPress,
        handleActiveChange,
        handleLockChange,
        handleMediaOrderChange,
        handleSelectMediaForVariant,
        handleRemoveMediaFromVariant,
        handleSetMainMedia,
        validateVariants
    } = useProductVariants(product, setProduct, showNotification, activeVariantId);
    
    // Fetch product when component mounts or productId changes
    useEffect(() => {
        if (productId) {
            fetchProduct(productId);
        } else {
            // If no productId, redirect to list page
            navigate("/cms/product");
        }
    }, [productId, navigate]);
    
    // Fetch product by ID from API
    const fetchProduct = async (id) => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/product/${id}`);
            
            if (response.data.isSuccess) {
                const productData = response.data.data;
                
                // Kiểm tra xem sản phẩm đã có slug ban đầu hay chưa
                setInitiallyHadSlug(!!productData.seoSlug);
                
                const productAttributes = productData.attributes || [];
                const productVariants = productData.variants || [];
                
                setProduct({
                    productId: productData.id,
                    headline: productData.name,
                    intro: productData.intro || "",
                    content: productData.description || "",
                    seoTitle: productData.seoTitle || "",
                    seoDescription: productData.seoDescription || "",
                    seoCanonical: productData.seoCanonical || "",
                    slug: productData.seoSlug || "",
                    price: productData.price ? productData.price.toString() : "",
                    // Map API media fields to UI media fields
                    medias: (productData.media || []).map(media => ({
                        id: media.id,
                        url: media.media_url,
                        type: media.media_type,
                        filename: media.media_url.split('/').pop(), // Extract filename from URL
                        filesize: media.media_size || 0,
                        sortOrder: media.sort_order || 0,
                        created_at: media.created_at
                    })),
                    attributes: productAttributes,
                    variants: productVariants
                });
                
                // Initialize temporary attributes and variants with the same data
                setTempAttributes(JSON.parse(JSON.stringify(productAttributes)));
                setTempVariants(JSON.parse(JSON.stringify(productVariants)));
                
                // If we have attributes but no variants, generate them
                if (productAttributes.length > 0 && productVariants.length === 0) {
                    const newVariants = generateVariantsFromAttributes(productAttributes);
                    setProduct(prev => ({
                        ...prev,
                        variants: newVariants
                    }));
                    setTempVariants(newVariants);
                }
            } else {
                showNotification("Product not found", "error");
                // Redirect to list page if product not found
                navigate("/cms/product");
            }
        } catch (error) {
            console.error("Error fetching product:", error);
            showNotification("Failed to load product data", "error");
            navigate("/cms/product");
        } finally {
            setLoading(false);
        }
    };
    
    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    
    // Generate slug from text
    const generateSlug = (text) => {
        if (!text) return '';
        
        // Loại bỏ dấu tiếng Việt
        let str = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        
        // Chuyển về chữ thường
        str = str.toLowerCase();
        
        // Thay thế ký tự đặc biệt và khoảng trắng bằng dấu gạch ngang
        str = str.replace(/[^a-z0-9]+/g, '-');
        
        // Loại bỏ dấu gạch ngang ở đầu và cuối
        str = str.replace(/^-+|-+$/g, '');
        
        return str;
    };
    
    // Handle input changes for product fields
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for price field (ensure it's a valid number)
        if (name === 'price') {
            // Allow empty string or valid positive numbers (can include decimals)
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setProduct({
                    ...product,
                    [name]: value
                });
            }
        } else {
            // Normal handling for text fields
            setProduct(prev => {
                const updatedProduct = {
                    ...prev,
                    [name]: value
                };
                
                // Chỉ tự động tạo slug khi headline thay đổi và ban đầu không có slug
                if (name === 'headline' && !initiallyHadSlug) {
                    updatedProduct.slug = generateSlug(value);
                }
                
                return updatedProduct;
            });
        }
    };
    
    // Generate slug manually
    const handleGenerateSlug = () => {
        if (product.headline) {
            setProduct(prev => ({
                ...prev,
                slug: generateSlug(prev.headline)
            }));
            showNotification(t('product.detail.slugGenerated'), "success");
        } else {
            showNotification(t('product.detail.noHeadlineForSlug'), "error");
        }
    };
    
    // Handle saving the product
    const handleSave = async () => {
        setSaving(true);
        try {
            // Validate required fields
            if (!product.headline || product.headline.trim() === '') {
                showNotification(t('product.detail.productName') + " " + t('common.required'), "error");
                setSaving(false);
                return;
            }
            
            // Validate variants
            const { isValid, errorMessage } = validateVariants();
            if (!isValid) {
                showNotification(errorMessage, "error");
                setSaving(false);
                return;
            }
            
            // Prepare product data for API
            const productToUpdate = {
                id: product.productId,
                name: product.headline.trim(),
                intro: product.intro || "",
                description: product.content || "",
                seoTitle: product.seoTitle || "",
                seoDescription: product.seoDescription || "",
                seoCanonical: product.seoCanonical || "",
                seoSlug: product.slug || "",
                // Include media if needed by API
                media: product.medias
            };
            
            const response = await axios.post(`${API_BASE_URL}/product/update`, productToUpdate, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                }
            });
            
            if (response.data.isSuccess) {
                showNotification(t('product.detail.saveSuccess'), "success");
            } else {
                throw new Error(response.data.message || "Failed to update product");
            }
        } catch (error) {
            console.error("Error saving product:", error);
            showNotification(error.message || t('product.detail.saveError'), "error");
        } finally {
            setSaving(false);
        }
    };
    
    // Go back to product list
    const handleBack = () => {
        navigate("/cms/product");
    };
    
    // Show notification
    function showNotification(message, severity) {
        setNotification({
            open: true,
            message,
            severity
        });
    }
    
    // Close notification
    const handleCloseNotification = () => {
        setNotification({
            ...notification,
            open: false
        });
    };
    
    // Handle opening the delete confirmation modal
    const handleOpenDeleteModal = (mediaId) => {
        const media = product.medias.find(m => m.id === mediaId);
        if (media) {
            setDeleteModal({
                open: true,
                mediaId,
                filename: media.filename,
                isLoading: false
            });
        }
    };
    
    // Handle the media deletion process
    const handleDeleteMedia = (mediaId) => {
        // Set loading state
        setDeleteModal(prev => ({
            ...prev,
            isLoading: true
        }));
        
        // Call the hook function with callback
        handleRemoveMedia(mediaId, () => {
            // Use a small timeout to ensure the modal closes after all other state updates
            setTimeout(() => {
                setDeleteModal({
                    open: false,
                    mediaId: null,
                    filename: null,
                    isLoading: false
                });
            }, 300);
        });
    };
    
    // Update openAttributesPanel function
    const handleOpenAttributesPanel = () => {
        // Initialize the temporary attributes with current product attributes
        setTempAttributes(JSON.parse(JSON.stringify(product.attributes)));
        setTempVariants(JSON.parse(JSON.stringify(product.variants)));
        
        // If attributes are saved, there are no unsaved changes
        const isViewingAttributes = product.attributes && product.attributes.length > 0 && !unsavedAttributeChanges;
        setUnsavedAttributeChanges(false);
        
        // Reset form if not in view mode
        if (!isViewingAttributes) {
            setNewAttribute({ name: "", values: "" });
            setEditingAttributeIndex(-1);
        }
        
        openAttributesPanel();
    };
    
    // Update closeAttributesPanel function
    const handleCloseAttributesPanel = () => {
        // Just close the panel without confirmation or saving
        closeAttributesPanel();
    };
    
    // Handle discarding attribute changes
    const handleDiscardAttributeChanges = () => {
        // Close the confirmation dialog
        setAttributeConfirmDialog({ open: false, mode: 'save' });
        // Reset unsaved changes flag
        setUnsavedAttributeChanges(false);
        // Close the attributes panel
        closeAttributesPanel();
    };
    
    // Handle saving attributes with loading state
    const handleSaveAttributesWithLoading = async () => {
        // Set saving state
        setSaving(true);
        
        // Show processing notification
        showNotification(t('product.detail.processingAttributes', 'Processing attributes...'), "info");
        
        try {
            // Call the hook function to save attributes
            const success = await handleSaveAttributes();
            
            if (success) {
                // Close the confirmation dialog and attributes panel
                setAttributeConfirmDialog({ open: false, mode: 'save' });
                closeAttributesPanel();
                
                // Show success notification
                showNotification(t('product.detail.attributesSaved', 'Attributes saved successfully'), "success");
            }
        } catch (error) {
            console.error("Error saving attributes:", error);
            showNotification(error.message || t('product.detail.attributeSaveError', 'Failed to save attributes'), "error");
        } finally {
            setSaving(false);
        }
    };

    // Implement the updateVariantMediaOrder function
    const updateVariantMediaOrder = async (payload) => {
        try {
            // Call the API endpoint for updating media order
            const response = await axios.post(
                `${API_BASE_URL}/product-variant/variant/media/order`, 
                payload
            );
            
            if (response.data.isSuccess) {
                // If successful, we've already updated the state in the handleMediaOrderChange function
                return response.data;
            } else {
                throw new Error(response.data.message || "Failed to update media order");
            }
        } catch (error) {
            console.error("Error in updateVariantMediaOrder:", error);
            showNotification(error.message || "Error updating media order", "error");
            throw error;
        }
    };

    return (
        <LayoutCms>
            <Box sx={{ padding: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <PageHeader 
                            title={product.headline || t('product.detail.title')} 
                            onBack={handleBack} 
                            onSave={handleSave}
                            saving={saving}
                            openMediaPanel={openMediaPanel}
                        />
                        
                        <Paper sx={{ mb: 3 }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={tabValue} onChange={handleTabChange} aria-label="product tabs">
                                    <Tab 
                                        icon={<Icon name="fileContent" />} 
                                        iconPosition="start" 
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                                {t('product.detail.info')}
                                            </Box>
                                        }
                                        id="product-tab-0" 
                                        aria-controls="product-tabpanel-0" 
                                    />
                                    <Tab 
                                        icon={<Icon name="settings" />} 
                                        iconPosition="start" 
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                                <Icon name="cogwheel" size={16} />
                                                {t('product.detail.config')}
                                            </Box>
                                        }
                                        id="product-tab-1" 
                                        aria-controls="product-tabpanel-1" 
                                    />
                                </Tabs>
                            </Box>
                            
                            {/* Information Tab */}
                            <TabPanel value={tabValue} index={0}>
                                <ProductInfoTab 
                                    product={product} 
                                    onChange={handleInputChange} 
                                    saving={saving}
                                    initiallyHadSlug={initiallyHadSlug}
                                    onGenerateSlug={handleGenerateSlug}
                                    t={t}
                                />
                            </TabPanel>
                            
                            {/* Configuration Tab */}
                            <TabPanel value={tabValue} index={1}>
                                <ProductConfigTab 
                                    product={product}
                                    onOpenAttributesPanel={handleOpenAttributesPanel}
                                    onEditAttribute={handleEditAttribute}
                                    onRemoveAttribute={handleRemoveAttribute}
                                    onPriceCellClick={handlePriceCellClick}
                                    onPriceChange={handlePriceChange}
                                    onPriceUpdate={handlePriceUpdate}
                                    onPriceKeyPress={handlePriceKeyPress}
                                    onPrediscountPriceCellClick={handlePrediscountPriceCellClick}
                                    onPrediscountPriceChange={handlePrediscountPriceChange}
                                    onPrediscountPriceUpdate={handlePrediscountPriceUpdate}
                                    onPrediscountPriceKeyPress={handlePrediscountPriceKeyPress}
                                    onActiveChange={handleActiveChange}
                                    onLockChange={handleLockChange}
                                    onMediaOrderChange={handleMediaOrderChange}
                                    onRemoveMediaFromVariant={handleRemoveMediaFromVariant}
                                    onSetMainMedia={handleSetMainMedia}
                                    tempPriceValue={tempPriceValue}
                                    editingPriceVariantId={editingPriceVariantId}
                                    tempPrediscountPriceValue={tempPrediscountPriceValue}
                                    editingPrediscountPriceVariantId={editingPrediscountPriceVariantId}
                                    setActiveVariantId={setActiveVariantId}
                                    openMediaPanel={openMediaPanel}
                                    updateVariantMediaOrder={updateVariantMediaOrder}
                                    isAttributesSaved={!unsavedAttributeChanges}
                                    t={t}
                                />
                            </TabPanel>
                        </Paper>
                    </>
                )}
            </Box>
            
            {/* Media Panel */}
            <MediaPanel
                isOpen={isMediaPanelOpen}
                onClose={() => {
                    closeMediaPanel();
                    setActiveVariantId(null);
                }}
                medias={product.medias}
                variants={product.variants}
                activeVariantId={activeVariantId}
                onSelectMediaForVariant={handleSelectMediaForVariant}
                selectedFiles={selectedFiles}
                uploading={uploading}
                onFileSelect={handleFileSelect}
                onFileChange={handleFileChange}
                onFileUpload={handleFileUpload}
                onCancelUpload={handleCancelUpload}
                onClearSelectedFiles={handleClearSelectedFiles}
                onOpenDeleteModal={handleOpenDeleteModal}
                fileInputRef={fileInputRef}
                t={t}
            />
            
            {/* Attributes Panel */}
            <AttributesPanel
                isOpen={isAttributesPanelOpen}
                onClose={handleCloseAttributesPanel}
                tempAttributes={tempAttributes}
                newAttribute={newAttribute}
                editingAttributeIndex={editingAttributeIndex}
                unsavedAttributeChanges={unsavedAttributeChanges}
                onAddAttribute={handleAddAttribute}
                onEditAttribute={handleEditAttribute}
                onRemoveAttribute={handleRemoveAttribute}
                onCancelEdit={handleCancelEdit}
                onAttributeInputChange={handleAttributeInputChange}
                onSaveAttributes={() => setAttributeConfirmDialog({ open: true, mode: 'save' })}
                saving={saving}
                isViewMode={product.attributes && product.attributes.length > 0 && !unsavedAttributeChanges}
                t={t}
            />
            
            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                open={deleteModal.open}
                filename={deleteModal.filename}
                onClose={() => setDeleteModal({ open: false, mediaId: null, filename: null, isLoading: false })}
                onDelete={() => handleDeleteMedia(deleteModal.mediaId)}
                isLoading={deleteModal.isLoading}
                t={t}
            />
            
            {/* Attribute Confirmation Dialog */}
            <AttributeConfirmDialog
                open={attributeConfirmDialog.open}
                mode={attributeConfirmDialog.mode}
                onClose={() => setAttributeConfirmDialog({ ...attributeConfirmDialog, open: false })}
                onSave={handleSaveAttributesWithLoading}
                onDiscard={handleDiscardAttributeChanges}
                saving={saving}
                t={t}
            />
            
            {/* Notifications */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity} 
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </LayoutCms>
    );
};

// Export with auth protection
export default withAuth(CmsProductDetailPage);